import React, { useState, useRef, useEffect } from "react";
import { 
  FiCalendar, 
  FiRefreshCw, 
  FiGrid, 
  FiActivity, 
  FiMic, 
  FiBarChart2 
} from "react-icons/fi";
import { BiChevronDown } from "react-icons/bi";
import FadeIn from "react-fade-in";
import { motion, AnimatePresence } from "framer-motion";
import ThemedButton from "../../../atom/ThemedButton/themedButton";
import TranscriptionOverview from "./transcriptionOverview";
import TranscriptionSTT from "./transcriptionSTT";
import TranscriptionAnalytics from "./transcriptionAnalytics";
import TranscriptionProcessing from "./transcriptionProcessing";
import Lottiefy from "../../../atom/lottie/lottie";
import * as loaderLottie from "../../../utils/lottie/loaderLottie.json";
import { getTranscriptionAnalytics } from "../../../services/api";

// ─── Helpers ────────────────────────────────────────────────────────────────
const getTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const getYesterdayStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return "";
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const TABS = [
  { id: "overview", label: "Overview", icon: <FiGrid size={16} /> },
  { id: "stt", label: "Speech to Text", icon: <FiMic size={16} /> },
  { id: "analytics", label: "LLM Analytics", icon: <FiBarChart2 size={16} /> },
  { id: "processing", label: "Processing", icon: <FiActivity size={16} /> },
];

const Transcription = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  const todayStr = getTodayStr();
  const yesterdayStr = getYesterdayStr();

  const [selectedDate, setSelectedDate] = useState(`Today, ${formatDate(todayStr)}`);
  const [customDate, setCustomDate] = useState(todayStr);

  const dateOptions = [
    { label: `Today, ${formatDate(todayStr)}`, key: "today" },
    { label: `Yesterday, ${formatDate(yesterdayStr)}`, key: "yesterday" },
    { label: "Custom", key: "custom" },
  ];

  const startDate =
    selectedDate.startsWith("Custom")
      ? customDate
      : selectedDate.startsWith("Yesterday")
        ? yesterdayStr
        : todayStr;
  const endDate = startDate;

  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [overviewData, setOverviewData] = useState<any>(null);
  const [sttData, setSttData] = useState<any>(null);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [processingData, setProcessingData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setFetchError(false);
      try {
        const [overviewRes, sttRes, analyticsRes, processingRes] = await Promise.all([
          getTranscriptionAnalytics("dashboard", startDate),
          getTranscriptionAnalytics("stt", startDate),
          getTranscriptionAnalytics("analytics", startDate),
          getTranscriptionAnalytics("logs", startDate),
        ]);
        
        setOverviewData(overviewRes?.data ?? overviewRes);
        setSttData(sttRes?.data ?? sttRes);
        setAnalyticsData(analyticsRes?.data ?? analyticsRes);
        setProcessingData(processingRes?.data ?? processingRes);
      } catch (err) {
        console.error("Error fetching transcription analytics:", err);
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, refreshTrigger]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);
  const handleDateChange = (opt: (typeof dateOptions)[0]) => {
    setSelectedDate(opt.label);
    setDropdownOpen(false);
  };

  return (
    <div className="py-3 overflow-auto h-full w-full">
      <div className="flex flex-col h-[calc(100vh-125px)] w-full p-8 bg-[#f8fafc] overflow-auto rounded-md">
        {/* ── Header ── */}
        <div className="flex justify-between items-center mb-10 -mt-4 border-b border-[#e5dddd92] pb-4">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
              Transcription Insight
            </h1>
            <p className="text-sm text-slate-500 mt-1 font-medium">Real-time transcription performance and analytics engine</p>
          </div>
          <ThemedButton
            children={"Refresh Data"}
            theme={"primary"}
            click={() => setRefreshTrigger((prev) => prev + 1)}
          />
        </div>

        {/* ── Tabs + Date-range selector ── */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-10">
          {/* Enhanced Tabs - Responsive Container */}
          <div className="relative flex items-center p-1 bg-slate-100/80 backdrop-blur-sm rounded-[18px] border border-slate-200/60 shadow-sm overflow-x-auto overflow-y-hidden no-scrollbar max-w-full w-fit">
            <div className="flex flex-nowrap min-w-max">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      relative px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold transition-all duration-300 flex items-center gap-2 z-10 whitespace-nowrap
                      ${isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-700"}
                    `}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute inset-0 bg-white rounded-[14px] shadow-[0_2px_8px_rgba(0,0,0,0.06)] border border-slate-200/50"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <span className={`relative z-20 ${isActive ? "text-[#feba12]" : "text-slate-400"}`}>
                      {tab.icon}
                    </span>
                    <span className="relative z-20">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            {/* Custom Date Input */}
            {selectedDate.startsWith("Custom") && (
              <div className="flex items-center gap-2 bg-white border border-[#ccc] rounded-xl px-3 py-2 shadow-sm text-sm">
                <span className="text-xs text-gray-500 font-semibold uppercase">
                  Date:
                </span>
                <input
                  type="date"
                  value={customDate}
                  max={todayStr}
                  onChange={(e) => {
                    setCustomDate(e.target.value);
                  }}
                  className="text-sm font-bold text-[#334155] border-none outline-none bg-transparent cursor-pointer focus:ring-0 focus:outline-none"
                />
              </div>
            )}

            {/* date picker dropdown */}
            <div className="relative w-full sm:min-w-[220px]" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen((p) => !p)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm font-medium border border-[#ccc] rounded-xl shadow-sm transition-colors bg-white focus:outline-none"
              >
                <div className="flex items-center gap-2">
                  <FiCalendar size={14} className="text-[#feba12]" />
                  <span className="text-left text-gray-700">
                    {selectedDate.startsWith("Custom")
                      ? `Custom: ${formatDate(customDate)}`
                      : selectedDate}
                  </span>
                </div>
                <BiChevronDown
                  size={16}
                  className={`text-gray-400 transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {dropdownOpen && (
                <div className="absolute left-0 right-0 mt-1 w-full border border-[#ccc] rounded-xl shadow-lg z-50 overflow-hidden bg-[#fff] py-1.5 animate-fadeIn">
                  {dateOptions.map((opt) => {
                    const isSelected =
                      opt.key === "custom"
                        ? selectedDate.startsWith("Custom")
                        : selectedDate === opt.label;
                    return (
                      <FadeIn key={opt.key}>
                        <button
                          onClick={() => handleDateChange(opt)}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                            isSelected
                              ? "bg-[#fef4e9] text-[#feba12] font-semibold"
                              : "hover:bg-gray-50 text-gray-600"
                          }`}
                        >
                          {opt.label}
                        </button>
                      </FadeIn>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Lottiefy loop={true} json={loaderLottie} height={100} width={100} />
          </div>
        ) : fetchError ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 min-h-[300px]">
            <p className="text-gray-700 font-semibold text-lg">Data Unavailable</p>
            <p className="text-gray-400 text-sm text-center max-w-sm">
              We couldn&apos;t load the transcription analytics data. Please try again.
            </p>
            <button
              onClick={() => setRefreshTrigger((prev) => prev + 1)}
              className="mt-2 px-5 py-2 text-sm font-semibold bg-[#feba12] text-white rounded-lg hover:bg-[#e5a80e] transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {activeTab === "overview" && (
              <TranscriptionOverview 
                startDate={startDate} 
                endDate={endDate} 
                refreshTrigger={refreshTrigger} 
                data={overviewData}
              />
            )}
            {activeTab === "processing" && (
              <TranscriptionProcessing 
                startDate={startDate} 
                endDate={endDate} 
                refreshTrigger={refreshTrigger} 
                data={processingData}
              />
            )}
            {activeTab === "stt" && (
              <TranscriptionSTT 
                startDate={startDate} 
                endDate={endDate} 
                refreshTrigger={refreshTrigger} 
                data={sttData}
              />
            )}
            {activeTab === "analytics" && (
              <TranscriptionAnalytics 
                startDate={startDate} 
                endDate={endDate} 
                refreshTrigger={refreshTrigger} 
                data={analyticsData}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Transcription;
